import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

import { DCustomFormControl } from "../../entities/custom-form-control";
import { DFormControl } from "../../entities/form-control";
import { DFormGroup } from "../../entities/form-group";
import { FormFieldFactory } from "../../services/form-field.factory";
import { I18nService } from "../../services/i18n.service";
import * as utils from "../../utils/utils";

@Component({
	selector: "ngx-dynamic-form",
	styleUrls: ["dynamic-form.component.scss"],
	template: `
		<div class="dynamic-form" [formGroup]="group">
			@for (field of config; track field) {
				<ng-container dynamicField [config]="field" [group]="group" />
			}
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DynamicFormComponent implements OnInit {
	private i18nService = inject(I18nService);
	private formFieldFactory = inject(FormFieldFactory);
	private fb = inject(UntypedFormBuilder);

	@Input() group: UntypedFormGroup = this.fb.group({});
	@Input() data: any;
	@Input() disabled: boolean;

	private formConfig: [DFormControl | DFormGroup];

	@Input()
	set controls(controls: DCustomFormControl[]) {
		if (!controls) {
			return;
		}

		for (const control of controls) {
			this.formFieldFactory.register(control);
		}
	}

	@Input()
	set language(language: string) {
		this.i18nService.setLanguage(language);
	}

	@Input()
	set config(config: [DFormControl | DFormGroup]) {
		this.formConfig = config;

		// Reset the group if we have a new config and parse it
		this.group = this.fb.group({});
		this.parseConfig(this.group, config);
	}

	get config() {
		return this.formConfig;
	}

	ngOnInit() {
		if (this.data) {
			this.group.patchValue(this.data);
		}

		if (this.disabled) {
			this.group.disable();
		}
	}

	private getValidators(config: any) {
		const validators = [];

		if (config.required !== undefined) {
			validators.push(Validators.required);
		}

		if (config.min !== undefined) {
			validators.push(Validators.min(config.min));
		}

		if (config.max !== undefined) {
			validators.push(Validators.max(config.max));
		}

		if (config.minLength !== undefined) {
			validators.push(Validators.minLength(config.minLength));
		}

		if (config.maxLength !== undefined) {
			validators.push(Validators.maxLength(config.maxLength));
		}

		return validators;
	}

	private parseConfig(parent: UntypedFormGroup, config: [DFormControl | DFormGroup]) {
		for (const control of config) {
			const validators = this.getValidators(control);

			if (utils.isGroup(control)) {
				const group = this.fb.group({}, validators);
				this.parseConfig(group, control.children);
				parent.addControl(control.name, group);
			} else {
				parent.addControl(control.name, this.fb.control(control.value, validators));
			}
		}
	}
}
